/**
 * Setup — V108-6-4 (design pass 2026-10-11, docs/v10.8/setup-screens-design.md). Route: /w/:slug/app/setup.
 *
 *   Sources       what is connected, in one line (partial multi-account sources are amber, AC-3)
 *   Needs attention   V108-6-6 warnings, each with an action
 *   Coverage      per program: "N of M seen · K to go", the four-part bar, the status pill
 *   Setup steps   the enabler list (program filter, agent box, groups, no-source by owner)
 *   Manage sources    the connection forms (security posture, collectors, the CI check, other sources)
 *
 * The onboarding checklist and the inventory slot hints retired into the setup steps (enablers).
 * Coverage, the steps and Today read one count source (GET /coverage + GET /enablers).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigationType, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, Field, PageHeader, Select, Spinner } from '@aigovops/design-system';
import { onboardingHref } from '../lib/onboarding-model';
import { CiSetupSteps } from '../components/setup/CiSetupSteps';
import { useWorkspace } from '../lib/workspace';
import { useOptionalAuth } from '../lib/auth';
import { usePrograms } from '../lib/workspace-navigation';
import { generateGacConfig, listConnectors, listWorkspaceSystems, type ConnectorRow, type GacConfigResponse, type WsSystemRow } from '../lib/api';
import { runOnboardingConnector } from '../lib/lifecycle-api';
import { ReconnectGitHub } from '../components/ReconnectGitHub';
import { GitHubReturnAlert } from '../components/GitHubConnect';
import { getGitHubConnection } from '../lib/lifecycle-api';
import { queryKeys, useInvalidate } from '../lib/query-keys';
import { getMyHats, type EnablerItem } from '../lib/setup-api';
import { agentSteps, groupSteps, movesNothing, sourcesLine, type SourceConnector } from '../lib/setup-model';
import { PostureConnections, POSTURE_KIND_SET, relativeTime } from '../components/PostureConnections';
import { CustomerCollectors } from '../components/CustomerCollectors';
import { ProviderSuggestions } from '../components/ProviderSuggestions';
import { EnablerList, FixGoneNotice } from '../components/setup/EnablerList';
import { AgentBox } from '../components/setup/AgentBox';
import { NoSourceList } from '../components/setup/NoSourceList';
import { SetupWarnings } from '../components/setup/SetupWarnings';
import { EnvironmentChoice } from '../components/setup/SetupGaps';
import { Glyph, orList, ProgramCoverageBlock, programShort, State, statusFromCard } from '../components/setup/parts';
import { useSetupToast } from '../components/setup/feedback';
import { usePeople, useRefreshSetup, useSetupData, type SetupFreshnessState } from '../components/setup/use-setup';
import { lifecycleDate } from './stages/lifecycle-view';
import { SweepSetupRow } from '../components/sweep/SweepNotice';
import './stages/lifecycle.css';
import '../components/setup/setup.css';

/** "owner/repo" from a GitHub connection's config, so rows (and their Reconnect buttons) can be told apart. */
function repoOfConnector(c: { kind: string; config_json?: string }): string | null {
  if (c.kind !== 'github' || !c.config_json) return null;
  try { const v = JSON.parse(c.config_json) as { owner?: string; repo?: string }; return v.owner && v.repo ? `${v.owner}/${v.repo}` : null; } catch { return null; }
}

export function Setup() {
  const { workspace, role } = useWorkspace();
  const slug = workspace.slug, prefix = `/w/${slug}/app`;
  const approver = role === 'owner' || role === 'admin';
  const [params, setParams] = useSearchParams();
  // V108-8-20: following a link to the SAME address (a router Link: PUSH / REPLACE with identical search) re-opens the
  // linked step. Review (a11y + flow High): never on other navigations — a hash jump (#manage-sources, a POP with a
  // new key), or a param cleanup (?connect, ?github: the search changes) must not pull focus back to the step.
  const location = useLocation();
  const navType = useNavigationType();
  const stepNav = useRef({ key: location.key, search: location.search, n: 0 });
  if (stepNav.current.key !== location.key) {
    const same = stepNav.current.search === location.search;
    stepNav.current = { key: location.key, search: location.search, n: stepNav.current.n + (same && navType !== 'POP' ? 1 : 0) };
  }
  const showAllSteps = useCallback(() => setParams((prev) => { const next = new URLSearchParams(prev); next.delete('program'); return next; }, { replace: true }), [setParams]);
  const connectKind = params.get('connect');
  const clearConnect = useCallback(() => setParams((prev) => { const next = new URLSearchParams(prev); next.delete('connect'); return next; }, { replace: true }), [setParams]);
  const program = params.get('program');
  // 2026-10-12: back from a GitHub Reconnect (?github=reconnected | failed&reason=…). Read once into its own state,
  // shown as an Alert at the top of the page (Other sources sits far below), then the params are cleared.
  // V108-8-1: the reason's own words and its one action (GitHubReturnAlert).
  const [githubReturn, setGithubReturn] = useState(() => params.get('github') === 'failed' || params.get('github') === 'reconnected'
    ? { github: params.get('github'), reason: params.get('reason'), owner: params.get('owner'), merged: params.get('merged') } : null);
  const githubStatus = useQuery({ queryKey: queryKeys.githubConnection(slug), queryFn: () => getGitHubConnection(slug), retry: false, enabled: !!githubReturn });
  useEffect(() => {
    if (!params.has('github')) return;
    setParams((prev) => { const next = new URLSearchParams(prev); for (const k of ['github', 'connector', 'reason', 'owner']) next.delete(k); return next; }, { replace: true });
  }, [params, setParams]);
  const backTo = params.get('back');
  // M4 / V102-8-3: a Connect started on a system page or a program's Gaps row keeps a way back (only to a page in this workspace).
  const back = backTo && new RegExp(`^/w/${slug.replace(/[^a-z0-9-]/gi, '')}/app/(systems/[^/?#]+|programs/[a-z0-9-]+/prepare\\?view=gaps(#gap-[A-Za-z0-9-]+)?)$`).test(backTo)
    ? { to: backTo, label: (params.get('back_label') ?? 'system').slice(0, 80) } : null;

  const { enablers, coverage, freshness, retry } = useSetupData(slug);
  // V108-8-2: every live connector shows in Sources (a GitHub App connection has no connect step).
  const connectorsQ = useQuery({ queryKey: queryKeys.connectors(slug), queryFn: () => listConnectors(slug), retry: false });
  const refresh = useRefreshSetup(slug);
  const invalidate = useInvalidate();
  const onChanged = useCallback(async () => { await refresh(); invalidate('connector:changed', slug); }, [refresh, invalidate, slug]);
  const programs = usePrograms(slug);
  const people = usePeople(slug);
  const viewerId = useOptionalAuth()?.user?.id ?? '';
  const hats = useQuery({ queryKey: ['assignments-me', slug], queryFn: () => getMyHats(slug), retry: false, staleTime: 60_000 });
  const canChange = approver || (hats.data?.responsibilities ?? []).includes('program_lead');
  const [toastNode, toast] = useSetupToast();
  const today = new Date().toISOString().slice(0, 10);

  const list = enablers.data?.enablers ?? [];
  const covPrograms = (coverage.data?.programs ?? []).filter((c) => c.objectives_in_scope > 0);
  const cardOf = (k: string) => programs.data?.programs.find((c) => c.program === k);
  const filterable = covPrograms.map((c) => c.program);
  const filter = program && filterable.includes(program) ? program : null;
  const { shown, total } = useMemo(() => groupSteps(list, filter), [list, filter]);
  const agent = agentSteps(list);
  const preview = covPrograms.reduce((n, c) => n + (c.preview_only ?? 0), 0);
  const headingRef = useRef<HTMLHeadingElement>(null);
  // V109-1-19 (H5): a link to one step (?step= / ?enabler=) scrolls and focuses that row once the list has
  // loaded (EnablerList); only the agent box scrolls here.
  useEffect(() => { if (params.get('agent')) document.getElementById('steps')?.scrollIntoView?.({ block: 'start' }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return <div className="v75-lifecycle su-page">
    <PageHeader title="Setup" subtitle="What GAC Pro can see, and the steps that let it see more." />
    <SetupFreshness freshness={freshness} onRetry={retry} />
    {back && <Link className="v101-back" to={back.to}><span aria-hidden="true">← </span>{`Back to ${back.label}`}</Link>}
    <nav className="su-jump" aria-label="Jump to"><ul>
      <li><a href="#sources">Sources</a></li>
      <li><a href="#coverage">Coverage</a></li>
      <li><a href="#steps">Setup steps</a></li>
      <li><a href="#manage-sources">Manage sources</a></li>
    </ul></nav>
    {githubReturn ? <GitHubReturnAlert slug={slug} github={githubReturn.github} reason={githubReturn.reason} owner={githubReturn.owner} merged={githubReturn.merged} status={githubStatus.data ?? null}
      returnTo={`${prefix}/setup`} onDismiss={() => setGithubReturn(null)} /> : null}

    {enablers.isLoading || coverage.isLoading ? <Spinner label="Loading setup" /> : enablers.error || coverage.error ? (
      <Alert variant="error"><p>Setup could not load.</p><Button onClick={() => { void enablers.refetch(); void coverage.refetch(); }}>Retry</Button></Alert>
    ) : <>
      <SourcesLine list={list} connectors={connectorsQ.data?.connectors ?? []} slug={slug} approver={approver} onChanged={onChanged} announce={toast} />
      {/* V109-5-2 (PD-6): the latest sweep's inventory, one click from Setup for every workspace. */}
      <SweepSetupRow slug={slug} prefix={prefix} />
      <SetupWarnings slug={slug} enablers={list} approver={approver} onChanged={onChanged} toast={toast} />

      <section id="coverage" className="su-card" aria-labelledby="su-cov-h">
        <div className="su-card-head">
          <h2 id="su-cov-h">Coverage</h2>
          {preview > 0 && <p className="su-preview-line"><span className="su-chip" data-kind="preview"><Glyph kind="eye" />Preview · doesn't count</span>{preview} more seen in staging</p>}
        </div>
        {covPrograms.length ? <div className="su-cov-pair">
          {covPrograms.map((c) => <ProgramCoverageBlock key={c.program} cov={c} enablers={list} status={statusFromCard(cardOf(c.program), c, today)} today={today} prefix={prefix}
            heading="h3" legendSeen noSourceHref={`${prefix}/setup?program=${c.program}#nosrc-${c.program}`} />)}
        </div> : freshness.computing ? <SetupWorking what="coverage" freshness={freshness} onRetry={retry} />
          : <p className="su-hint">No program has objectives in scope yet. <Link to={`${prefix}/programs`}>Choose a program</Link> to see what GAC Pro can check for it.</p>}
      </section>

      <section id="steps" aria-labelledby="su-steps-h" style={{ display: 'grid', gap: 'var(--sp-4)' }}>
        <div className="su-steps-head">
          <h2 id="su-steps-h" ref={headingRef}>Setup steps</h2>
          {filterable.length > 1 && <nav className="su-filter" aria-label="Show steps for">
            <Link to="?#steps" aria-current={!filter ? 'page' : undefined}>All</Link>
            {filterable.map((k) => <Link key={k} to={`?program=${k}#steps`} aria-current={filter === k ? 'page' : undefined}>{programShort(k)}</Link>)}
          </nav>}
        </div>
        {list.length > 0 || filter || !freshness.computing ? <p className="su-hint" style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', flexWrap: 'wrap' }}>
          {filter ? `Showing ${programShort(filter)} steps: ${shown} of ${total}` : `All ${total} steps · most unlocked for the least time first`}
          {filter && <Link className="su-linkish" to="?#steps">Show all</Link>}
        </p> : null}
        <AgentBox steps={agent} workspaceName={workspace.name} cliVersion={enablers.data?.cli_version ?? null} openOnLoad={params.get('agent') === 'open'} />
        {list.length ? <EnablerList key={params.get('step') ?? ''} slug={slug} enablers={list} updatedAt={enablers.dataUpdatedAt} people={people} viewer={{ user_id: viewerId, role }}
          canChange={canChange} program={filter} onChanged={onChanged} cliVersion={enablers.data?.cli_version ?? null} settled={enablers.isFetchedAfterMount}
          link={{ step: params.get('step'), enabler: params.get('enabler'), subject: params.get('subject') }} navKey={String(stepNav.current.n)} onShowAll={showAllSteps} />
          : <>{params.get('enabler') && enablers.isFetchedAfterMount ? <FixGoneNotice slug={slug} code={params.get('enabler')!} subject={params.get('subject')} approver={approver} /> : null}
              {freshness.computing
                ? <SetupWorking what="setup steps" freshness={freshness} onRetry={retry} />
                : <p className="su-hint">No setup steps right now. Every source GAC Pro can use for your programs is connected. <a href="#manage-sources">Manage sources</a></p>}</>}
        <NoSourceList slug={slug} prefix={prefix} programs={(filter ? [filter] : filterable).filter((k) => (covPrograms.find((c) => c.program === k)?.manual_only ?? 0) > 0)} people={people}
          viewerId={viewerId} canAssign={canChange} openProgram={params.get('evidence') ?? params.get('program')} onChanged={onChanged} toast={toast} />
      </section>
    </>}

    <section id="manage-sources" aria-labelledby="su-manage-h" style={{ display: 'grid', gap: 'var(--sp-4)' }}>
      <h2 id="su-manage-h" style={{ margin: 0, fontSize: 'var(--fs-xl)' }}>Manage sources</h2>
      <ProviderSuggestions slug={slug} editable={approver} quiet />
      <PostureConnections slug={slug} editable={approver} connectKind={connectKind} onConnectOpened={clearConnect} showSuggested back={back} quiet />
      <CustomerCollectors slug={slug} editable={approver} />
      <CiGovernanceCheck slug={slug} editable={approver}
        notCountedFor={[...new Set(list.filter((e) => e.code.startsWith('ci-gate:') && movesNothing(e)).flatMap((e) => e.unlocks.not_taken_by!.map((n) => n.program)))]} />
      <OtherSources slug={slug} editable={approver} />
    </section>
    {toastNode}
  </div>;
}

const SOURCE_GLYPH = { done: 'check', partial: 'clock', muted: 'eye', attention: 'alert', progress: 'clock' } as const;
/** One line of what is connected: done (green), partial multi-account (amber, AC-3), preview only, plus (V108-8-2)
 * every other live connector: verified once read, "first read pending" before, "sign-in expired" when it needs Reconnect. */
export function SourcesLine({ list, connectors, slug, approver = false, onChanged, announce }: {
  list: readonly EnablerItem[]; connectors: readonly SourceConnector[];
  /** V108-8-18 (c): with these, a preview source carries the environment control in place. */
  slug?: string; approver?: boolean; onChanged?: () => Promise<unknown>; announce?: (m: string) => void;
}) {
  const sources = sourcesLine(list, connectors, approver);
  return <section id="sources" className="su-card su-sources" aria-labelledby="su-src-h">
    <div style={{ display: 'flex', gap: 'var(--sp-4)', alignItems: 'center', flexWrap: 'wrap' }}>
      <h2 id="su-src-h">Sources</h2>
      {sources.length ? <ul>{sources.map((s) => <li key={s.key}><State tone={s.tone} glyph={SOURCE_GLYPH[s.tone]}>{s.detail ? `${s.name} · ${s.detail}` : s.name}</State>
        {/* V108-8-13: a source that doesn't count yet says where it is fixed. */}
        {s.preview && slug && onChanged && announce ? <EnvironmentChoice slug={slug} connectorId={s.preview.connector_id} kind={s.preview.kind} environment={s.preview.environment}
          approver={approver} onChanged={onChanged} announce={announce} compact />
          : s.action ? (s.action.href.startsWith('?') ? <Link className="su-linkish" to={s.action.href} data-part="source-action">{s.action.label}<span className="su-sr">{`: ${s.name}`}</span></Link>
          : <a className="su-linkish" href={s.action.href} data-part="source-action">{s.action.label}<span className="su-sr">{`: ${s.name}`}</span></a>) : null}</li>)}</ul>
        : <span className="su-hint">Nothing connected yet.</span>}
    </div>
    <a className="su-btn" href="#steps">Add a source</a>
  </section>;
}

/** Sources outside the security-posture forms (GitHub, IdP discovery): run them, or connect from onboarding. */
function OtherSources({ slug, editable }: { slug: string; editable: boolean }) {
  const q = useQuery({ queryKey: queryKeys.connectors(slug), queryFn: () => listConnectors(slug), retry: false });
  const invalidate = useInvalidate();
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState('');
  const rows: ConnectorRow[] = (q.data?.connectors ?? []).filter((c) => !POSTURE_KIND_SET.has(c.kind));
  const names: Record<string, string> = { github: 'GitHub', gitlab: 'GitLab', okta: 'Okta', entra: 'Microsoft Entra', local_upload: 'Local upload' };
  return <Card><section className="v75-panel" aria-labelledby="connections-title">
    <div className="v75-page-heading"><h2 id="connections-title">Other sources</h2>{editable && <Link className="v75-action" to={onboardingHref(slug, 'connect')}>Connect a source</Link>}</div>
    <div role="status" aria-live="polite" className="v75-muted">{notice}</div>
    {q.isLoading ? <Spinner label="Loading sources" /> : q.error ? <Alert variant="error"><p>Sources could not load.</p><Button onClick={() => void q.refetch()}>Retry</Button></Alert>
      : rows.length === 0 ? <p className="v75-muted">No other sources yet.</p>
      : <ul className="v75-connections">{rows.map((c) => { const repo = repoOfConnector(c); return <li key={c.connector_id}><div><strong>{names[c.kind] ?? c.kind.replace(/_/g, ' ')}{repo ? ` · ${repo}` : ''}</strong><small>Configured {lifecycleDate(c.configured_at)}</small><small>{c.last_run_at ? `Last read ${lifecycleDate(c.last_run_at)}` : 'Not read yet'}</small></div>
        {/* 2026-10-12: an expired GitHub sign-in gets Reconnect (reuses this connection), not a disabled Read now. */}
        <div className="v75-actions">{c.needs_reconnect ? <><Badge className="v75-status warning">Sign-in expired</Badge><ReconnectGitHub slug={slug} connectorId={c.connector_id} target={repo} variant="secondary" /></> : <><Badge className={`v75-status ${c.status === 'active' ? 'success' : 'neutral'}`}>{c.status === 'active' ? 'Active' : c.status}</Badge>
          {editable && <Button variant="secondary" disabled={!!busy || c.status !== 'active'} onClick={async () => { setBusy(c.connector_id); try { const r = await runOnboardingConnector(slug, c.connector_id); setNotice(r.workflow_id ? 'Read queued. New evidence appears after processing.' : 'Read requested.'); invalidate('connector:changed', slug); } catch (e) { setNotice(e instanceof Error ? e.message : 'The read could not start.'); } finally { setBusy(null); } }}>{busy === c.connector_id ? 'Requesting…' : 'Read now'}</Button>}</>}</div></li>; })}</ul>}
    {!editable && <p className="v75-muted">A workspace owner or administrator manages connections.</p>}
  </section></Card>;
}

/** CI Governance Check section — generates the governance workflow for ONE system from
 * the server and walks through it with the shared CiSetupSteps (V108-1-3). A system can be
 * preselected with `?ci_system=` (the link an engineer sends an admin). */
export function CiGovernanceCheck({ slug, editable, notCountedFor = [] }: {
  slug: string; editable: boolean;
  /** V108-8-8: programs where no objective uses CI evidence (from the ci-gate step's `unlocks.not_taken_by`). */
  notCountedFor?: readonly string[];
}) {
  const [params] = useSearchParams();
  const [systems, setSystems] = useState<WsSystemRow[] | null>(null);
  const [systemsError, setSystemsError] = useState(false);
  const [loadKey, setLoadKey] = useState(0);
  const [selectedSystem, setSelectedSystem] = useState(params.get('ci_system') ?? '');
  const [config, setConfig] = useState<GacConfigResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [shareNote, setShareNote] = useState('');
  const generateButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let active = true;
    setSystemsError(false);
    listWorkspaceSystems(slug).then(r => {
      if (!active) return;
      setSystems(r.systems);
      setSelectedSystem(prev => (prev && r.systems.some(s => s.system_id === prev)) ? prev : r.systems.length === 1 ? r.systems[0].system_id : '');
    }).catch(() => { if (active) setSystemsError(true); });
    return () => { active = false; };
  }, [slug, loadKey]);

  const generate = async () => {
    if (!selectedSystem) return;
    setBusy(true); setError(''); setConfig(null);
    try {
      setConfig(await generateGacConfig(slug, selectedSystem));
    } catch (e) { setError(e instanceof Error ? e.message : 'Configuration could not be generated. Please retry.'); }
    finally { setBusy(false); }
  };

  const shareWithAdmin = async () => {
    const url = new URL(`/w/${encodeURIComponent(slug)}/app/setup`, window.location.origin);
    if (selectedSystem) url.searchParams.set('ci_system', selectedSystem);
    try { await navigator.clipboard.writeText(url.toString()); setShareNote('Link copied. Send it to a workspace owner or administrator.'); }
    catch { setShareNote(`Send this link to a workspace owner or administrator: ${url.toString()}`); }
  };

  const system = systems?.find(s => s.system_id === selectedSystem) ?? null;

  return <Card><section className="v75-panel" aria-labelledby="ci-governance-title">
    <div className="v75-page-heading">
      <div>
        <h2 id="ci-governance-title">CI governance check</h2>
        <p className="v75-muted">Every build checks this system against its obligations and sends the result as evidence.</p>
        {notCountedFor.length > 0 && <p className="v75-muted" data-part="ci-not-counted">{`Results don't count toward ${orList(notCountedFor.map(programShort))}: no objective there uses CI evidence.`}</p>}
      </div>
    </div>

    {systemsError && <Alert variant="error"><p>Systems couldn't load.</p><Button variant="secondary" onClick={() => setLoadKey(k => k + 1)}>Retry</Button></Alert>}
    {systems !== null && systems.length === 0 && <p>No systems yet. <Link to={`/w/${slug}/app/systems/new`}>Add a system</Link></p>}

    {!config && systems !== null && systems.length > 1 && <Field id="ci-system-select" label="System">
      <Select id="ci-system-select" value={selectedSystem} onChange={e => { setSelectedSystem(e.target.value); setError(''); }}>
        <option value="">Choose a system</option>
        {systems.map(s => <option key={s.system_id} value={s.system_id}>{s.name}</option>)}
      </Select>
    </Field>}

    {!config && !!systems?.length && (editable ? <div className="v75-actions">
      <Button ref={generateButton} variant="secondary" disabled={busy || !selectedSystem} onClick={() => void generate()}>{busy ? 'Generating…' : 'Generate CI configuration'}</Button>
      {!selectedSystem && <span className="v75-muted">Choose a system first.</span>}
    </div> : <div className="v75-actions">
      <p className="v75-muted" style={{ margin: 0 }}>A workspace owner or administrator generates this.</p>
      <Button variant="secondary" onClick={() => void shareWithAdmin()}>Copy link for an admin</Button>
    </div>)}
    <p role="status" aria-live="polite" className="v75-muted" style={{ margin: 0 }}>{shareNote}</p>

    {error && <Alert variant="error">{error}</Alert>}

    {config && system && <>
      <CiSetupSteps slug={slug} systemId={system.system_id} systemName={system.name} config={config} />
      <Button variant="secondary" onClick={() => { setConfig(null); requestAnimationFrame(() => generateButton.current?.focus()); }}>Generate again</Button>
    </>}
  </section></Card>;
}

/**
 * V108-8-21: ONE quiet freshness line for the page (steps and coverage come from the same stored view):
 * "Checked 4 min ago", "Checked 4 min ago · updating…" while a newer view is computed, and — after a minute of
 * computing — "This is taking longer than usual." with the page's one Retry. A single polite live region says when an
 * update starts, lands, is retried or takes long; the ticking age itself is never announced.
 */
export function SetupFreshness({ freshness, onRetry }: { freshness: SetupFreshnessState; onRetry: () => void }) {
  const { computedAt, computing, stuck } = freshness;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { setNow(Date.now()); const i = window.setInterval(() => setNow(Date.now()), 30_000); return () => window.clearInterval(i); }, [computedAt]);
  const [said, setSaid] = useState('');
  const was = useRef<{ computing: boolean; stuck: boolean } | null>(null);
  const lineRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    const prev = was.current;
    was.current = { computing, stuck };
    const msg = stuck && !prev?.stuck ? 'Setup is taking longer than usual.'
      : prev?.stuck && !stuck && computing ? 'Retrying…'
      : prev?.computing && !computing ? 'Setup updated.'
      : prev && !prev.computing && computing ? 'Updating setup…'
      : !prev && computing && !computedAt ? 'Working out your setup…' : null;
    if (!msg) return;
    // Clear first and write on the next frame, so a repeated message is announced again (and not lost on mount).
    setSaid('');
    const f = window.requestAnimationFrame(() => setSaid(msg));
    return () => window.cancelAnimationFrame(f);
  }, [computing, stuck, computedAt]);
  const retry = () => { lineRef.current?.focus(); onRetry(); };
  return <>
    <p className="sr-only" aria-live="polite">{said}</p>
    {/* One element whatever it says, so focus stays on it when Retry turns the line back into the age. */}
    <p id="su-fresh" ref={lineRef} tabIndex={-1} className={stuck ? 'su-hint su-computing' : 'su-hint su-fresh'}>
      {stuck ? <>{computedAt ? `Checked ${relativeTime(computedAt, now)} · taking longer than usual` : 'This is taking longer than usual.'}<Button variant="secondary" onClick={retry}>Retry</Button></>
        : computedAt ? `Checked ${relativeTime(computedAt, now)}${computing ? ' · updating…' : ''}` : null}
    </p>
  </>;
}

/**
 * V108-8-21: an empty section while a view is being computed — never the "nothing here" text, which would read as
 * final. While the compute takes too long: "Not ready yet. Retry" (the same retry as the freshness line).
 */
export function SetupWorking({ what, freshness, onRetry }: { what: 'coverage' | 'setup steps'; freshness: SetupFreshnessState; onRetry: () => void }) {
  if (freshness.stuck) {
    // The same retry as the freshness line (focus moves there, where the outcome is said), for a reader who jumped here.
    const retry = () => { document.getElementById('su-fresh')?.focus(); onRetry(); };
    return <p className="su-hint">Not ready yet. <button type="button" className="su-linkish" onClick={retry}>Retry</button></p>;
  }
  return <p className="su-hint su-computing"><span aria-hidden="true"><Spinner size={14} label="" /></span>
    {freshness.computedAt ? `Updating your ${what}…` : `Working out your ${what}…`}</p>;
}
